'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import PropTypes from 'prop-types';

const CompetitionRules = ({ competition }) => {
  const [isClient, setIsClient] = useState(false);
  
  useEffect(() => {
    setIsClient(true);
  }, []);

  const rules = [
    "Participants must be 18 years or older to enter this competition.",
    "Each ticket purchased gives you one entry into the draw.",
    "The draw will take place on the specified date and time using a certified random number generator.",
    "Winners will be notified via email and SMS within 24 hours of the draw.",
    "Prizes must be claimed within 30 days of the draw date.",
    "This competition is open to residents of the United Kingdom only.",
    "No purchase necessary alternative entry method available upon request.",
    "All sales are final - no refunds except in case of competition cancellation.",
    "By entering, you agree to allow use of your name and image for promotional purposes."
  ];

  const getDrawInfo = () => {
    if (!isClient || !competition?.drawDate) {
      return [
        {
          icon: 'CalendarDaysIcon',
          label: 'Draw Date',
          value: 'Loading...'
        },
        {
          icon: 'ClockIcon',
          label: 'Draw Time',
          value: 'Loading...'
        },
        {
          icon: 'ComputerDesktopIcon',
          label: 'Draw Method',
          value: 'Certified Random Number Generator'
        },
        {
          icon: 'VideoCameraIcon',
          label: 'Live Stream',
          value: 'Available on our website and social media'
        }
      ];
    }

    return [
      {
        icon: 'CalendarDaysIcon',
        label: 'Draw Date',
        value: new Date(competition.drawDate)?.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      },
      {
        icon: 'ClockIcon',
        label: 'Draw Time',
        value: new Date(competition.drawDate)?.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short'
        })
      },
      {
        icon: 'ComputerDesktopIcon',
        label: 'Draw Method',
        value: 'Certified Random Number Generator'
      },
      {
        icon: 'VideoCameraIcon',
        label: 'Live Stream',
        value: 'Available on our website and social media'
      }
    ];
  };

  const drawInfo = getDrawInfo();

  return (
    <div className="space-y-6">
      {/* Draw Information */}
      <div className="bg-card rounded-[6px] border border-border p-6">
        <h3 className="text-lg font-semibold text-card-foreground mb-4">
          Draw Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {drawInfo?.map((info, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-accent/10 rounded-[6px] flex items-center justify-center">
                <Icon name={info?.icon} size={20} className="text-accent" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{info?.label}</p>
                <p className="text-sm text-muted-foreground">{info?.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Live Stream Notice */}
        <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-[6px]">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="EyeIcon" size={20} className="text-accent" />
            <span className="text-sm font-medium text-accent">Live Draw</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Watch the draw live on our social media channels. All draws are recorded and archived for transparency.
          </p>
        </div>
      </div>
      {/* Competition Rules */}
      <div className="bg-card rounded-[6px] border border-border p-6">
        <h3 className="text-lg font-semibold text-card-foreground mb-4">
          Competition Rules
        </h3>
        
        <div className="space-y-3">
          {rules?.map((rule, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-accent/10 rounded-full flex items-center justify-center mt-0.5">
                <span className="text-xs font-medium text-accent">{index + 1}</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {rule}
              </p>
            </div>
          ))}
        </div>

        {/* Important Notice */}
        <div className="mt-6 p-4 bg-warning/10 border border-warning/20 rounded-[6px]">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="ExclamationTriangleIcon" size={20} className="text-warning" />
            <span className="text-sm font-medium text-warning">Important Notice</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Please read all rules carefully before purchasing tickets. By participating, you acknowledge that you have read, understood, and agree to abide by all competition rules and terms.
          </p>
        </div>
      </div>
      {/* Winner Verification */}
      <div className="bg-card rounded-[6px] border border-border p-6">
        <h3 className="text-lg font-semibold text-card-foreground mb-4">
          Winner Verification Process
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-success/10 rounded-[6px] flex items-center justify-center">
              <Icon name="CheckCircleIcon" size={16} className="text-success" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Instant Notification</p>
              <p className="text-xs text-muted-foreground">
                Winners are notified immediately via email.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-success/10 rounded-[6px] flex items-center justify-center">
              <Icon name="IdentificationIcon" size={16} className="text-success" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Identity Verification</p>
              <p className="text-xs text-muted-foreground">
                Winners must provide valid ID to claim prizes
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-success/10 rounded-[6px] flex items-center justify-center">
              <Icon name="DocumentCheckIcon" size={16} className="text-success" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">Public Verification</p>
              <p className="text-xs text-muted-foreground">
                Winner details are published for transparency
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

CompetitionRules.propTypes = {
  competition: PropTypes?.shape({
    drawDate: PropTypes?.string?.isRequired
  })?.isRequired
};

export default CompetitionRules;